import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import type { ScreenerItem, Filter } from "@/types/screener";
import type { FilterLogic } from "@/services/api/screener";
import type { FilterLogicOp } from "../filterExpression";
import { useAuth } from "@/context/AuthContext";
import {
  getScreenerStateStorageKey,
  LEGACY_SCREENER_STATE_KEY,
} from "@/constants/screenerStorageKeys";

const OLD_PINNED_ITEMS_KEY = "screener-pinned-items";
const LEGACY_EDIT_MODE_KEY = "screener-edit-mode";

// Column IDs that represent name-like columns (for special treatment)
export const NAME_COLUMN_IDS = [
  "display_name",
  "short_name",
  "long_name",
  "name",
] as const;

// Selected item structure (formerly PinnedItem)
export interface SelectedItem {
  id: string;
  display_name: string;
  short_name?: string;
  long_name?: string;
  resource_type: string;
  resource_id: string;
  default_stream_type?: string;
  pinnedAt: number; // timestamp for ordering
}

/**
 * Preferences structure for screener user configuration.
 * All preferences are persisted to localStorage in the unified screener-state key.
 */
export interface ScreenerPreferences {
  /** Currently active view ID */
  activeViewId?: string;
  /** Currently active static list (watchlist/filter) ID */
  activeStaticListId?: string;
  /** Column to sort by */
  sortBy?: string;
  /** Sort direction */
  sortOrder?: "asc" | "desc";
  /** Whether to show benchmark rows */
  showBenchmarks?: boolean;
  /** Active filter criteria */
  activeFilters?: Filter[];
  /** Per-connector logic between adjacent filter pills */
  filterConnectors?: FilterLogicOp[];
  /** How multiple filters are combined */
  filterLogic?: FilterLogic;
  /** Current search term */
  searchTerm?: string;
  /** Current page number (1-indexed) */
  currentPage?: number;
  /** Number of items per page */
  pageSize?: number;
  /** Whether edit mode is enabled */
  isEditMode?: boolean;
  /** ID of the globally-preferred default view (loaded on app open) */
  defaultViewId?: string;
}

// Combined state structure
interface ScreenerState {
  selectedItems: SelectedItem[];
  quickAnalysisItems: SelectedItem[];
  isQuickAnalysisMode: boolean;
  preferences: ScreenerPreferences;
}

// Return type for the hook
interface UseScreenerStateReturn {
  // Selection
  selectedItems: SelectedItem[];
  selectedIds: Set<string>;
  selectItems: (items: ScreenerItem[]) => void;
  deselectItem: (id: string) => void;
  deselectAll: () => void;
  isSelected: (id: string) => boolean;

  // Quick Analysis
  quickAnalysisItems: SelectedItem[];
  quickAnalysisIds: Set<string>;
  isQuickAnalysisMode: boolean;
  setQuickAnalysisItems: (items: SelectedItem[]) => void;
  addToQuickAnalysis: (items: ScreenerItem[]) => void;
  removeFromQuickAnalysis: (id: string) => void;
  clearQuickAnalysis: () => void;
  enterQuickAnalysisMode: () => void;
  exitQuickAnalysisMode: () => void;
  isInQuickAnalysis: (id: string) => boolean;

  // Preferences
  preferences: ScreenerPreferences;
  updatePreference: <K extends keyof ScreenerPreferences>(
    key: K,
    value: ScreenerPreferences[K]
  ) => void;
  updatePreferences: (updates: Partial<ScreenerPreferences>) => void;
  clearPreferences: () => void;

  // Backward compatibility aliases for existing code
  pinnedItems: SelectedItem[];
  pinnedIds: Set<string>;
  pinItems: (items: ScreenerItem[]) => void;
  unpinItem: (id: string) => void;
  unpinAll: () => void;
  isPinned: (id: string) => boolean;
}

/**
 * Load screener state from localStorage, with migration from old format
 */
function loadScreenerState(userId: string | null): ScreenerState {
  const emptyState: ScreenerState = {
    selectedItems: [],
    quickAnalysisItems: [],
    isQuickAnalysisMode: false,
    preferences: {},
  };

  if (!userId || typeof window === "undefined") {
    return emptyState;
  }

  const storageKey = getScreenerStateStorageKey(userId);

  try {
    const stored =
      localStorage.getItem(storageKey) ??
      localStorage.getItem(LEGACY_SCREENER_STATE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed && typeof parsed === "object") {
        const state: ScreenerState = {
          selectedItems: Array.isArray(parsed.selectedItems)
            ? parsed.selectedItems
            : [],
          quickAnalysisItems: Array.isArray(parsed.quickAnalysisItems)
            ? parsed.quickAnalysisItems
            : [],
          isQuickAnalysisMode: parsed.isQuickAnalysisMode === true,
          preferences:
            parsed.preferences && typeof parsed.preferences === "object"
              ? parsed.preferences
              : {},
        };

        const legacyEditMode = localStorage.getItem(LEGACY_EDIT_MODE_KEY);
        if (legacyEditMode !== null && state.preferences.isEditMode === undefined) {
          state.preferences.isEditMode = legacyEditMode === "true";
          localStorage.setItem(storageKey, JSON.stringify(state));
          localStorage.removeItem(LEGACY_EDIT_MODE_KEY);
        } else {
          localStorage.setItem(storageKey, JSON.stringify(state));
        }
        localStorage.removeItem(LEGACY_SCREENER_STATE_KEY);

        return state;
      }
    }

    const legacyEditMode = localStorage.getItem(LEGACY_EDIT_MODE_KEY);
    const oldPinnedItems = localStorage.getItem(OLD_PINNED_ITEMS_KEY);
    if (oldPinnedItems) {
      const parsed = JSON.parse(oldPinnedItems);
      if (Array.isArray(parsed)) {
        const migratedState: ScreenerState = {
          selectedItems: parsed,
          quickAnalysisItems: [],
          isQuickAnalysisMode: false,
          preferences: {
            isEditMode: legacyEditMode === "true" ? true : legacyEditMode === "false" ? false : undefined,
          },
        };
        localStorage.setItem(storageKey, JSON.stringify(migratedState));
        localStorage.removeItem(OLD_PINNED_ITEMS_KEY);
        if (legacyEditMode !== null) {
          localStorage.removeItem(LEGACY_EDIT_MODE_KEY);
        }
        localStorage.removeItem(LEGACY_SCREENER_STATE_KEY);
        return migratedState;
      }
    }

    if (legacyEditMode !== null) {
      const migratedState: ScreenerState = {
        selectedItems: [],
        quickAnalysisItems: [],
        isQuickAnalysisMode: false,
        preferences: {
          isEditMode: legacyEditMode === "true",
        },
      };
      localStorage.setItem(storageKey, JSON.stringify(migratedState));
      localStorage.removeItem(LEGACY_EDIT_MODE_KEY);
      localStorage.removeItem(LEGACY_SCREENER_STATE_KEY);
      return migratedState;
    }
  } catch (error) {
    console.error("Error loading screener state from localStorage:", error);
  }

  return emptyState;
}

/**
 * Save screener state to localStorage
 */
function saveScreenerState(userId: string | null, state: ScreenerState): void {
  if (!userId || typeof window === "undefined") return;

  try {
    localStorage.setItem(getScreenerStateStorageKey(userId), JSON.stringify(state));
  } catch (error) {
    console.error("Error saving screener state to localStorage:", error);
  }
}

/**
 * Unified hook for managing screener state
 * Combines selected items and preferences in a single localStorage key
 */
export function useScreenerState(): UseScreenerStateReturn {
  const { user } = useAuth();
  const userId = user?.id ?? null;
  const [state, setState] = useState<ScreenerState>(() => loadScreenerState(userId));
  const previousUserIdRef = useRef<string | null>(userId);

  useEffect(() => {
    if (previousUserIdRef.current === userId) {
      return;
    }
    previousUserIdRef.current = userId;
    setState(loadScreenerState(userId));
  }, [userId]);

  // Sync to localStorage whenever state changes
  useEffect(() => {
    saveScreenerState(userId, state);
  }, [userId, state]);

  // Create a Set of selected IDs for O(1) lookup
  const selectedIds = useMemo(() => {
    return new Set(state.selectedItems.map((item) => item.id));
  }, [state.selectedItems]);

  // Check if an item is selected
  const isSelected = useCallback(
    (id: string): boolean => {
      return selectedIds.has(id);
    },
    [selectedIds]
  );

  // Select multiple items (from ScreenerItem format)
  const selectItems = useCallback((items: ScreenerItem[]) => {
    setState((prev) => {
      const existingIds = new Set(prev.selectedItems.map((p) => p.id));
      const newItems: SelectedItem[] = [];
      const now = Date.now();

      items.forEach((item) => {
        if (!existingIds.has(item.id)) {
          newItems.push({
            id: item.id,
            display_name:
              item.display_name ||
              item.short_name ||
              item.long_name ||
              item.name ||
              item.id,
            short_name: item.short_name,
            long_name: item.long_name,
            resource_type: item.resource_type || "product",
            resource_id:
              item.metadata?.resource_id || item.resource_id || item.id,
            default_stream_type: item.default_stream_type,
            pinnedAt: now,
          });
        }
      });

      if (newItems.length === 0) return prev;

      return {
        ...prev,
        selectedItems: [...prev.selectedItems, ...newItems],
      };
    });
  }, []);

  // Deselect a single item by ID
  const deselectItem = useCallback((id: string) => {
    setState((prev) => ({
      ...prev,
      selectedItems: prev.selectedItems.filter((item) => item.id !== id),
    }));
  }, []);

  // Clear all selected items
  const deselectAll = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selectedItems: [],
    }));
  }, []);

  // ============ Quick Analysis Functions ============

  // Create a Set of Quick Analysis IDs for O(1) lookup
  const quickAnalysisIds = useMemo(() => {
    return new Set(state.quickAnalysisItems.map((item) => item.id));
  }, [state.quickAnalysisItems]);

  // Check if an item is in Quick Analysis
  const isInQuickAnalysis = useCallback(
    (id: string): boolean => {
      return quickAnalysisIds.has(id);
    },
    [quickAnalysisIds]
  );

  // Set Quick Analysis items (replaces existing items)
  const setQuickAnalysisItems = useCallback((items: SelectedItem[]) => {
    setState((prev) => ({
      ...prev,
      quickAnalysisItems: items,
      isQuickAnalysisMode: items.length > 0,
    }));
  }, []);

  // Add items to Quick Analysis (from ScreenerItem format)
  const addToQuickAnalysis = useCallback((items: ScreenerItem[]) => {
    setState((prev) => {
      const existingIds = new Set(prev.quickAnalysisItems.map((p) => p.id));
      const newItems: SelectedItem[] = [];
      const now = Date.now();

      items.forEach((item) => {
        if (!existingIds.has(item.id)) {
          newItems.push({
            id: item.id,
            display_name:
              item.display_name ||
              item.short_name ||
              item.long_name ||
              item.name ||
              item.id,
            short_name: item.short_name,
            long_name: item.long_name,
            resource_type: item.resource_type || "product",
            resource_id:
              item.metadata?.resource_id || item.resource_id || item.id,
            default_stream_type: item.default_stream_type,
            pinnedAt: now,
          });
        }
      });

      if (newItems.length === 0) return prev;

      return {
        ...prev,
        quickAnalysisItems: [...prev.quickAnalysisItems, ...newItems],
      };
    });
  }, []);

  // Remove a single item from Quick Analysis
  const removeFromQuickAnalysis = useCallback((id: string) => {
    setState((prev) => {
      const newItems = prev.quickAnalysisItems.filter((item) => item.id !== id);
      return {
        ...prev,
        quickAnalysisItems: newItems,
        // Exit Quick Analysis mode if no items left
        isQuickAnalysisMode:
          newItems.length > 0 ? prev.isQuickAnalysisMode : false,
      };
    });
  }, []);

  // Clear all Quick Analysis items and exit mode
  const clearQuickAnalysis = useCallback(() => {
    setState((prev) => ({
      ...prev,
      quickAnalysisItems: [],
      isQuickAnalysisMode: false,
    }));
  }, []);

  // Enter Quick Analysis mode (usually copies selected items to quick analysis)
  const enterQuickAnalysisMode = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isQuickAnalysisMode: true,
    }));
  }, []);

  // Exit Quick Analysis mode without clearing items
  const exitQuickAnalysisMode = useCallback(() => {
    setState((prev) => ({
      ...prev,
      isQuickAnalysisMode: false,
    }));
  }, []);

  // ============ End Quick Analysis Functions ============

  // Update a single preference
  const updatePreference = useCallback(
    <K extends keyof ScreenerPreferences>(
      key: K,
      value: ScreenerPreferences[K]
    ) => {
      setState((prev) => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          [key]: value,
        },
      }));
    },
    []
  );

  // Update multiple preferences at once
  const updatePreferences = useCallback(
    (updates: Partial<ScreenerPreferences>) => {
      setState((prev) => ({
        ...prev,
        preferences: {
          ...prev.preferences,
          ...updates,
        },
      }));
    },
    []
  );

  // Clear all preferences
  const clearPreferences = useCallback(() => {
    setState((prev) => ({
      ...prev,
      preferences: {},
    }));
  }, []);

  // Memoize the return object to prevent unnecessary re-renders
  // Only create a new object reference when the values actually change
  return useMemo(
    () => ({
      // Selection
      selectedItems: state.selectedItems,
      selectedIds,
      selectItems,
      deselectItem,
      deselectAll,
      isSelected,

      // Quick Analysis
      quickAnalysisItems: state.quickAnalysisItems,
      quickAnalysisIds,
      isQuickAnalysisMode: state.isQuickAnalysisMode,
      setQuickAnalysisItems,
      addToQuickAnalysis,
      removeFromQuickAnalysis,
      clearQuickAnalysis,
      enterQuickAnalysisMode,
      exitQuickAnalysisMode,
      isInQuickAnalysis,

      // Preferences
      preferences: state.preferences,
      updatePreference,
      updatePreferences,
      clearPreferences,

      // Backward compatibility aliases
      pinnedItems: state.selectedItems,
      pinnedIds: selectedIds,
      pinItems: selectItems,
      unpinItem: deselectItem,
      unpinAll: deselectAll,
      isPinned: isSelected,
    }),
    [
      state.selectedItems,
      state.quickAnalysisItems,
      state.isQuickAnalysisMode,
      state.preferences,
      selectedIds,
      quickAnalysisIds,
      selectItems,
      deselectItem,
      deselectAll,
      isSelected,
      setQuickAnalysisItems,
      addToQuickAnalysis,
      removeFromQuickAnalysis,
      clearQuickAnalysis,
      enterQuickAnalysisMode,
      exitQuickAnalysisMode,
      isInQuickAnalysis,
      updatePreference,
      updatePreferences,
      clearPreferences,
    ]
  );
}

// Re-export types for backward compatibility
export type { SelectedItem as PinnedItem };
